/**
 * GenerationGrid — virtualized batch progress grid using VirtuosoGrid.
 * Only renders visible cells (~20-40) regardless of total item count.
 * Each cell shows the source thumbnail with a label strip; completed cells
 * crossfade to the generated image with a colored border.
 */

import { useCallback, useMemo, type MutableRefObject } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { motion } from 'motion/react';
import { useProjectStore } from '@/store/projectStore.ts';
import { parseAspectRatio } from '@/utils/aspectRatio.ts';
import styles from './GenerationGrid.module.css';

/** Structural type — accepts progressItems from useAiWorkspaceFlow (extra props ignored) */
interface GridItem {
  id: string;
  completed: boolean;
  sourceThumb?: string;
  label?: string;
  shotId?: number;
}

interface Props {
  items: GridItem[];
  completedMedia: Map<string, string>;
  isEnhance: boolean;
  /** Called when user taps a grid cell. Parent captures the rect for fly-in animation. */
  onCellClick: (itemId: string, shotId: number, cellEl: HTMLElement) => void;
  /** Parent-owned ref map so GenerationPreview can look up cell rects for fly-out exit. */
  cellRefsRef: MutableRefObject<Map<string, HTMLDivElement>>;
}

export function GenerationGrid({ items, completedMedia, isEnhance, onCellClick, cellRefsRef }: Props) {
  const project = useProjectStore(s => s.project);
  const ar = project ? parseAspectRatio(project.aspectRatio) : 16 / 9;
  const aspectStyle = useMemo(
    () => ({ aspectRatio: ar > 0 ? `${ar}` : '16 / 9' }),
    [ar],
  );

  const itemContent = useCallback((index: number) => {
    const item = items[index];
    if (!item) return null;
    const genUrl = completedMedia.get(item.id);
    const isCompleted = item.completed && !!genUrl;

    return (
      <div
        ref={el => {
          if (el) cellRefsRef.current.set(item.id, el);
          else cellRefsRef.current.delete(item.id);
        }}
        className={`${styles.gridCell} ${isCompleted ? styles.gridCellCompleted : ''}`}
        style={aspectStyle}
        onClick={e => {
          if (item.shotId != null) {
            onCellClick(item.id, item.shotId, e.currentTarget as HTMLElement);
          }
        }}
      >
        {/* Base layer: source thumbnail or pending label */}
        {item.sourceThumb ? (
          <img className={styles.cellThumb} src={item.sourceThumb} alt="" />
        ) : (
          <div className={styles.cellPending}>{item.label ?? '?'}</div>
        )}

        {/* Crossfade layer: generated image */}
        {isCompleted && (
          isEnhance ? (
            <motion.img
              className={styles.cellGenerated}
              src={genUrl}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            />
          ) : (
            <motion.video
              className={styles.cellGenerated}
              src={genUrl}
              muted
              autoPlay
              loop
              playsInline
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            />
          )
        )}

        {/* Label strip */}
        <div className={styles.labelStrip}>{item.label ?? '?'}</div>
      </div>
    );
  }, [items, completedMedia, isEnhance, aspectStyle, onCellClick, cellRefsRef]);

  return (
    <VirtuosoGrid
      totalCount={items.length}
      itemContent={itemContent}
      listClassName={styles.gridList}
      itemClassName={styles.gridItem}
      style={{ flex: 1, minHeight: 0, width: '100%', alignSelf: 'stretch' }}
    />
  );
}
